import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { fetchApi } from "../../Functions";
import { addSingleTask, removeSingleTask } from "../../../store/InspectionsTask";
import { removeSingleTaskLink, removeLinksForTask, addMultipleTaskLinks } from "../../../store/InspectionTaskLinks";
import { TInspectionTask } from "../../../types/types";
import { URLs } from "../../../Constants/Consts";
import { RootState } from "../../../store/store";

export type TaskFormData = {
    taskHeader: string;
    taskDescription: string;
    order: number;
};

/**
 * Hook for CRUD operations on inspection tasks
 * Provides add, edit, and remove functions with local Redux updates
 */
export function useInspectionTaskCRUD(activeInspectionId: number | null) {
    const dispatch = useDispatch();
    const { inspectionTaskLinks } = useSelector((state: RootState) => state.InspectionTaskLinks);

    // Helper to build task payload
    const buildTaskPayload = useCallback((data: TaskFormData, inspectionTaskId = 0) => ({
        inspectionTaskId,
        inspectionId: activeInspectionId,
        taskHeader: data.taskHeader,
        taskDescription: data.taskDescription,
        listOrder: data.order
    }), [activeInspectionId]);

    // Add task
    const addTask = useCallback(async (data: TaskFormData): Promise<boolean> => {
        if (!activeInspectionId) {
            toast.error("No active model selected");
            return false;
        }

        try {
            const res = await fetchApi(URLs.addInspectionTask(), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(buildTaskPayload(data))
            });
            const newTask = await res.json();

            dispatch(addSingleTask({
                inspectionTaskId: newTask?.itemId,
                inspectionId: activeInspectionId,
                taskHeader: data.taskHeader,
                taskDescription: data.taskDescription,
                listOrder: data.order,
            }));
            toast.success("InspectionTask added successfully");
            return true;
        } catch (error) {
            console.error("Error adding task:", {error});
            toast.error("Failed to add InspectionTask");
            return false;
        }
    }, [activeInspectionId, buildTaskPayload]);

    // Edit task - backend deletes old task and creates a new one with a new ID
    const editTask = useCallback(async (inspectionTaskId: number, data: TaskFormData): Promise<number | false> => {
        if (!activeInspectionId) {
            toast.error("No active model selected");
            return false;
        }

        try {
            const res = await fetchApi(URLs.editDeleteInspectionTask(inspectionTaskId), {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(buildTaskPayload(data, inspectionTaskId))
            });
            const result = await res.json();
            const newTaskId = result?.itemId;

            if (!newTaskId) {
                toast.error("Failed to get updated task ID from server");
                return false;
            }

            // Remove old task and add the new one with the updated ID
            dispatch(removeSingleTask(inspectionTaskId));
            dispatch(addSingleTask({
                inspectionTaskId: newTaskId,
                inspectionId: activeInspectionId,
                taskHeader: data.taskHeader,
                taskDescription: data.taskDescription,
                listOrder: data.order,
            }));

            // Remove old links and fetch fresh copies from the API
            dispatch(removeLinksForTask(inspectionTaskId));
            const linksRes = await fetchApi(URLs.getGroupedInspectionTaskLinks(newTaskId));
            const freshLinks = await linksRes.json();
            if (Array.isArray(freshLinks) && freshLinks.length > 0) {
                dispatch(addMultipleTaskLinks(freshLinks));
            }

            toast.success("InspectionTask updated successfully");
            return newTaskId;
        } catch {
            toast.error("Failed to edit InspectionTask");
            return false;
        }
    }, [activeInspectionId, buildTaskPayload, dispatch]);

    // Remove task and its associated links
    const removeTask = useCallback(async (task: TInspectionTask): Promise<boolean> => {
        try {
            // Find all associated task links before deleting
            const associatedLinks = inspectionTaskLinks.filter(
                link => link.inspectionTaskId === task.inspectionTaskId
            );
            
            // Delete all associated links from the API first
            if (associatedLinks.length > 0) {
                const linkDeletePromises = associatedLinks.map(async link => {
                    const res = await fetchApi(URLs.editDeleteInspectionTaskLink(link.itLinkId), { method: 'DELETE' });
                    if (!res.ok) {
                        const errorData = await res.json().catch(() => ({}));
                        throw new Error(errorData?.detail || `Failed to delete link ${link.itLinkId}`);
                    }
                    return res;
                });
                await Promise.all(linkDeletePromises);
            }
            
            // Then delete the task from the API
            const taskRes = await fetchApi(URLs.editDeleteInspectionTask(task.inspectionTaskId), { method: 'DELETE' });
            if (!taskRes.ok) {
                const errorData = await taskRes.json().catch(() => ({}));
                throw new Error(errorData?.detail || "Failed to delete task from API");
            }            
            // Remove all associated links from Redux
            associatedLinks.forEach(link => {
                dispatch(removeSingleTaskLink(link.itLinkId));
            });
            
            // Remove the task from Redux
            dispatch(removeSingleTask(task.inspectionTaskId));
            
            toast.success("InspectionTask and its links deleted");
            return true;
        } catch (error) {
            console.error("Error deleting task:", error);
            const err = error as Error & { detail?: string };
            toast.error(err?.detail || err?.message || "Failed to remove task");
            return false;
        }
    }, [inspectionTaskLinks, dispatch]);

    return {
        addTask,
        editTask,
        removeTask
    };
}
